import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Loader2, X, AlertTriangle } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { adminApi } from "@/lib/adminApi";

/**
 * SotCleanupModal — dry-run preview + confirm-apply flow for the SoT flag
 * cleanup endpoint. Resets rows that were incorrectly stamped admin_edited
 * by the historical "Mark All Verified Today" bug, while preserving rows
 * with legitimate hand-edits (identified via jurisdiction_change_log).
 *
 * Backend contract:
 *   GET  /admin/jurisdictions/sot-cleanup-preview   (dry-run, zero writes)
 *   POST /admin/jurisdictions/sot-cleanup           ({ confirm: true })
 */
export default function SotCleanupModal({ open, onClose, onApplied }) {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/jurisdictions/sot-cleanup-preview");
      setPlan(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load cleanup preview");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) { setPlan(null); load(); } }, [open]);

  const apply = async () => {
    setApplying(true);
    try {
      const { data } = await adminApi.post("/admin/jurisdictions/sot-cleanup",
        { confirm: true });
      toast.success(
        `Reset ${data.tax_reset_count} tax + ${data.privacy_reset_count} privacy row(s) back to CSV`,
      );
      onApplied?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cleanup failed");
    } finally {
      setApplying(false);
    }
  };

  const s = plan?.summary;
  const nothingToDo = s && s.tax_reset_count === 0 && s.privacy_reset_count === 0;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !applying && onClose()}>
      <DialogContent className="max-w-2xl bg-[var(--rg-bg)] border-[var(--rg-border)] text-[var(--rg-text-strong)]">
        <DialogHeader>
          <DialogTitle className="text-[var(--rg-text-strong)] flex items-center gap-2">
            <ShieldCheck className="w-5 h-5" />
            Reset SoT Flags
          </DialogTitle>
          <DialogDescription className="text-[var(--rg-text)] text-xs">
            Reset <code className="font-mono">source_of_truth</code> back to <code>csv</code> for
            rows that were incorrectly marked <code>admin_edited</code> by a routine timestamp
            action. Rows with real hand-edits (present in the change log) are preserved.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="py-6 text-sm text-[var(--rg-muted)]">
            <Loader2 className="w-4 h-4 inline animate-spin mr-2" /> Building preview…
          </div>
        )}

        {plan && (
          <div className="max-h-[55vh] overflow-y-auto" data-testid="sot-cleanup-preview">
            <div className="grid sm:grid-cols-2 gap-3 mb-4">
              <CountCard label="Tax — will reset to CSV" value={s.tax_reset_count} color="var(--rg-sage)" />
              <CountCard label="Tax — preserved (had real edits)" value={s.tax_keep_count} color="var(--rg-amber)" />
              <CountCard label="Privacy — will reset to CSV" value={s.privacy_reset_count} color="var(--rg-sage)" />
              <CountCard label="Privacy — preserved (had real edits)" value={s.privacy_keep_count} color="var(--rg-amber)" />
            </div>

            {nothingToDo ? (
              <div className="rounded-md border border-[var(--rg-border)] bg-[var(--rg-surface)] p-4 text-center text-sm text-[var(--rg-muted)]" data-testid="sot-cleanup-nothing">
                Nothing to do — no rows are currently over-flagged as admin_edited.
              </div>
            ) : (
              <>
                <ListBlock
                  title="Tax — will reset to CSV"
                  items={plan.tax.will_reset}
                  color="var(--rg-sage)"
                  testid="sot-cleanup-tax-reset"
                />
                <ListBlock
                  title="Tax — preserved (hand-edited via Deploy)"
                  items={plan.tax.will_keep}
                  color="var(--rg-amber)"
                  testid="sot-cleanup-tax-keep"
                />
                <ListBlock
                  title="Privacy — will reset to CSV"
                  items={plan.privacy.will_reset}
                  color="var(--rg-sage)"
                  testid="sot-cleanup-privacy-reset"
                />
                <ListBlock
                  title="Privacy — preserved (hand-edited via Deploy)"
                  items={plan.privacy.will_keep}
                  color="var(--rg-amber)"
                  testid="sot-cleanup-privacy-keep"
                />
              </>
            )}
          </div>
        )}

        <DialogFooter className="border-t border-[var(--rg-border)] pt-4">
          <div className="flex flex-wrap gap-2 w-full justify-end">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={applying}
              className="border-[var(--rg-border)] text-[var(--rg-text-strong)] hover:bg-[var(--rg-surface)]"
              data-testid="sot-cleanup-cancel"
            >
              <X className="w-4 h-4 mr-1.5" /> Cancel
            </Button>
            <Button
              onClick={apply}
              disabled={!plan || nothingToDo || applying}
              className="bg-[var(--rg-sage)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-sage-hover)] font-semibold disabled:opacity-40"
              data-testid="sot-cleanup-apply"
            >
              {applying
                ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                : <ShieldCheck className="w-4 h-4 mr-1.5" />}
              Reset {s ? s.tax_reset_count + s.privacy_reset_count : 0} row(s) to CSV
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CountCard({ label, value, color }) {
  return (
    <div className="rounded-md border p-3 bg-[var(--rg-surface)]" style={{ borderColor: color }}>
      <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--rg-text)]">{label}</div>
      <div className="text-2xl font-heading font-bold mt-1" style={{ color }}>{value}</div>
    </div>
  );
}

function ListBlock({ title, items, color, testid }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="mb-3" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wider font-bold mb-1.5" style={{ color }}>
        {title} ({items.length})
      </div>
      <div className="flex flex-wrap gap-1">
        {items.map((abbr) => (
          <span
            key={abbr}
            className="text-[11px] font-mono px-2 py-0.5 rounded-sm border"
            style={{ color, borderColor: color, background: color + "15" }}
          >
            {abbr}
          </span>
        ))}
      </div>
    </div>
  );
}
